import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CalendarClock } from "lucide-react";
import { ROLE_LABELS } from "@/lib/format";
import { ProjectIdentityFields, useClients, matchClient } from "@/components/ProjectIdentity";

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const toISO = (d) => (d ? new Date(d).toISOString() : null);

// The lead's disciplines on the fixed scope letters; plain "electrical" has to be picked (DC or AC).
const leadScopes = (lead) => {
  const disc = [lead?.discipline, ...((lead?.scopes || []).map((s) => s?.discipline))].filter(Boolean);
  const out = new Set();
  disc.forEach((d) => {
    const k = String(d).toLowerCase();
    if (k === "mechanical" || k === "both") out.add("mechanical");
    if (["civil", "substation", "commissioning", "full_epc", "tm_labor", "electrical_dc", "electrical_ac"].includes(k)) out.add(k);
  });
  return [...out];
};

// Strip "150MW", a state or scope words out of a lead's project name so only the site name is left.
const siteFromLead = (lead) => (lead?.project || lead?.project_name || "")
  .replace(/\b\d+(?:\.\d+)?\s*(?:mw|mwdc|mwac|mwp)\b/ig, "")
  .replace(/\b(mechanical|mech|electrical|elec|civil|substation|commissioning|epc|t&m)\b/ig, "")
  .replace(/[-–—,]\s*$/g, "").replace(/\s{2,}/g, " ").trim();

// The estimating hand-off: client + site + scopes (the number is minted here), the key dates, and the estimator.
export function ConvertLeadDialog({ lead, open, onOpenChange, onConverted }) {
  const [users, setUsers] = useState([]);
  const clients = useClients();
  const [form, setForm] = useState({ due_date: "", mobilization_date: "", assigned_to: "" });
  const [ident, setIdent] = useState({ client_id: null, project: "", scopes: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ due_date: toDateInput(lead?.due_date), mobilization_date: toDateInput(lead?.mobilization_date), assigned_to: lead?.assigned_to || "" });
    setIdent({ client_id: lead?.client_id || null, project: siteFromLead(lead), scopes: leadScopes(lead) });
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (lead?.lead_id && !lead?.assigned_to) {
      api.get(`/leads/${lead.lead_id}/team`).then((r) => {
        const est = (r.data.members || []).find((m) => m.role === "estimator" || m.role === "assistant_estimator");
        if (est) setForm((s) => ({ ...s, assigned_to: s.assigned_to || est.user_id }));
      }).catch(() => {});
    }
  }, [open, lead]);

  useEffect(() => {
    if (!open || ident.client_id || !clients.length) return;
    const c = matchClient(clients, lead?.company);
    if (c) setIdent((s) => ({ ...s, client_id: c.client_id }));
  }, [open, clients, lead, ident.client_id]);

  const estimators = users.filter((u) => ["owner", "admin", "chief_estimator", "estimator"].includes(u.role));

  const convert = async () => {
    if (!ident.client_id) { toast.error("Pick the client from the list."); return; }
    if (!ident.project.trim()) { toast.error("Enter the site name."); return; }
    if (!ident.scopes.length) { toast.error("Pick at least one scope (plain Electrical needs DC or AC)."); return; }
    setSaving(true);
    try {
      const res = await api.post(`/leads/${lead.lead_id}/convert`, {
        due_date: toISO(form.due_date), mobilization_date: toISO(form.mobilization_date), assigned_to: form.assigned_to || null,
        client_id: ident.client_id, project: ident.project.trim(), scopes: ident.scopes,
      });
      toast.success(res.data.sibling_bid_ids?.length ? `Handed off — ${1 + res.data.sibling_bid_ids.length} scope bids under ${res.data.project_number}` : `Handed off to estimating — ${res.data.bid_number || ""}`);
      onOpenChange(false);
      onConverted?.(res.data.bid_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not convert");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ k, label }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`convert-${k}`} type="date" value={form[k]}
        onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="convert-lead-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#003E87]" /> Hand off to estimating
          </DialogTitle>
          <DialogDescription>
            One project per client and site, one bid per scope. The project number is minted now; the bid names are composed for you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[62vh] overflow-y-auto pr-1">
          <ProjectIdentityFields value={ident} onChange={setIdent} multiple idPrefix="convert" clients={clients} />
          <div className="grid grid-cols-2 gap-3">
            <F k="due_date" label="Bid due" />
            <F k="mobilization_date" label="Mobilization (schedule start)" />
          </div>
          <div>
            <Label className="text-xs">Assign estimator</Label>
            <Select value={form.assigned_to} onValueChange={(v) => setForm((s) => ({ ...s, assigned_to: v }))}>
              <SelectTrigger data-testid="convert-assignee"><SelectValue placeholder="Who owns this estimate?" /></SelectTrigger>
              <SelectContent>
                {estimators.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {(u.name || u.email)} — {ROLE_LABELS[u.role] || u.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Optional — defaults to you if left blank. You can reassign inside the bid.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="convert-confirm-btn" onClick={convert} disabled={saving}
            className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
            Convert to estimate <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
